import { XMarkIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import Button from '../../../components/form-control/Button';
import useLayoutHook from '../../../hooks/layoutHook';
import { useState } from 'react';
import { fuelAdminCloseVendorSales, getAllFuelSaleTransactions } from '../../../api/requests';
import GTable from '../../../components/elements/GTable';
import { useQuery, useMutation } from '@tanstack/react-query';
import { showToast } from '../../../utils/miscFunctions';

interface UnclosedSaleDetailsProps {
  selectedSale: any;
  onClose: () => void;
  onProceed: () => void;
  show: boolean;
  columns: any;
}

const UnclosedSaleDetails = ({
  selectedSale,
  onClose,
  show,
  onProceed,
  columns
}: UnclosedSaleDetailsProps) => {
  const { SideDrawer } = useLayoutHook();
  const [saleTransactions, setSaleTransactions] = useState<any>([]);

  // Fetch sale transactions
  const fetchSaleTransactions = async () => {
    if (selectedSale) {
      try {
        const res = await getAllFuelSaleTransactions(selectedSale.id);
        setSaleTransactions(res);
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Query for fetching sale transactions
  const { data, isLoading: transactionsLoading } = useQuery({
    queryKey: [selectedSale?.id],
    queryFn: fetchSaleTransactions,
    enabled: !!selectedSale
  });

  // Mutation for approving the vendor's daily sale
  const { mutate: approveVendorDailySale, isPending: approveLoading } = useMutation({
    mutationFn: (saleId: string) => fuelAdminCloseVendorSales(saleId),
    onSuccess: (response: any) => {
      showToast.success(response?.message ?? 'Vendor sale approved successfully');
      onClose();
      onProceed();
    },
    onError: (error: any) => {
      showToast.error(error?.description ?? 'Failed to approve Vendor sale');
    }
  });

  return (
    <SideDrawer show={show}>
      <div className="p-5 bg-white h-screen border border-primary-border rounded">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold">
              {selectedSale.submittedBy?.firstName[0] ?? '-'}
              {selectedSale.submittedBy?.lastName[0]}
            </div>
            <div>
              <h1 className="font-semibold">
                {selectedSale.submittedBy?.firstName} {selectedSale.submittedBy?.lastName}
              </h1>
            </div>
          </div>
          <XMarkIcon className="cursor-pointer h-5 w-5 text-red-500" onClick={onClose} />
        </div>

        <hr className="w-full my-3" />

        <div className="mt-2 text-sm">
          <div className="flex items-center gap-2">
            <h1 className="font-semibold">Sale Date:</h1>
            <span>{moment(selectedSale.salesMadeOn).format('Do MMM YY')}</span>
          </div>

          <div className="py-2 flex items-center gap-2">
            <h1 className="font-semibold">Amount:</h1>
            <span>{selectedSale.amount ?? '-'}</span>
          </div>

          <div className="py-2 flex items-center gap-2">
            <h1 className="font-semibold">Quantity:</h1>
            <span>{selectedSale.quantity ?? '-'} L</span>
          </div>

          <div className="py-2 flex items-center gap-2">
            <h1 className="font-semibold">Submitted On:</h1>
            <span>
              {selectedSale.submittedOn
                ? moment(selectedSale.submittedOn).format('Do MMM YY, h:mm')
                : '-'}
            </span>
          </div>
          <div className="py-2 flex items-center gap-2">
            <h1 className="font-semibold">Received On:</h1>
            <span>
              {selectedSale.receivedOn
                ? moment(selectedSale.receivedOn).format('Do MMM YY, h:mm')
                : '-'}
            </span>
          </div>
        </div>
        {selectedSale.status === "PENDING_CLOSE"  && (
          <div className="flex justify-end">
            <Button
              curved
              bgColor="primary"
              label="Approve"
              onClick={() => approveVendorDailySale(selectedSale.id)}
              disabled={approveLoading}
              loading={approveLoading}
            />
          </div>
        )}

        <div className="mt-4">
          <GTable
            columns={columns}
            data={saleTransactions ?? []}
            showSearch={false}
            tableType="transaction"
            columnClasses=""
            classes="h-fit"
            loading={transactionsLoading}
          />
        </div>
      </div>
    </SideDrawer>
  );
};

export default UnclosedSaleDetails;
